import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import Table, { compareStrings } from 'apollo-react/components/Table';
import Plus from 'apollo-react-icons/Plus';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import ActionCell from './editor/controls/table/ActionCell';
import EditableCell from './editor/controls/table/EditableCell';
import EditableSelectCell from './editor/controls/table/EditableSelectCell';
import { createStringSearchFilter } from '../../components/SavedSearchesTable/utils';
import TextFieldFilter from '../../components/TableFilters/TextInputFilter';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getUITA } from '../../utils/commonUtils';
import {
  addEntityRelation,
  deleteEntityRelation,
  editEntityRelation,
  validateEntityDesigner,
} from '../actions/entity-designer.actions';
import {
  RELATION_TYPES,
  RELATIONS_CHILD_ENTITY_NAME,
  RELATIONS_PARENT_OBJECT_KEY,
  RELATIONS_PARENT_TABLE_OBJECT_KEY_COLUMN,
  RELATIONS_RELATION_TYPE,
  RELATIONS_RELATION_TYPE_LABEL,
  RELATIONS_SUB_ENTITY_ID,
  TABLE_RELATIONS_CONTROL_TYPE,
} from '../constants/entity-designer.constants';
import { EntityContext } from '../context';
import { getRelations } from '../selectors/entity-designer.selectors';
import { customStringSearchFilter } from '../utils/table-filters.utils';

const useStyles = makeStyles({
  addButtonContainer: {
    textAlign: 'right',
    paddingTop: '8px !important',
    paddingRight: '15px !important',
  },
  childEntityWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
});

const columns = [
  {
    header: 'Child Entity Name',
    accessor: RELATIONS_CHILD_ENTITY_NAME,
    sortFunction: compareStrings,
    customCell: EditableSelectCell('entitiesList'),
    filterFunction: createStringSearchFilter(RELATIONS_CHILD_ENTITY_NAME),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Relationship Type',
    accessor: RELATIONS_RELATION_TYPE,
    sortFunction: compareStrings,
    customCell: EditableSelectCell('relationTypes'),
    filterFunction: customStringSearchFilter(RELATIONS_RELATION_TYPE_LABEL),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Link Source',
    accessor: RELATIONS_PARENT_OBJECT_KEY,
    sortFunction: compareStrings,
    customCell: EditableCell,
    filterFunction: createStringSearchFilter(RELATIONS_PARENT_OBJECT_KEY),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Link Target (Parent Table Column)',
    accessor: RELATIONS_PARENT_TABLE_OBJECT_KEY_COLUMN,
    sortFunction: compareStrings,
    customCell: EditableCell,
    filterFunction: createStringSearchFilter(RELATIONS_PARENT_TABLE_OBJECT_KEY_COLUMN),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Sub Entity Id',
    accessor: RELATIONS_SUB_ENTITY_ID,
    sortFunction: compareStrings,
    customCell: EditableCell,
    filterFunction: createStringSearchFilter(RELATIONS_SUB_ENTITY_ID),
    filterComponent: TextFieldFilter,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

export const relationsValidationProps = [
  {
    prop: RELATIONS_CHILD_ENTITY_NAME,
    errorLabel: 'Child Entity Name',
    required: true,
    unique: true,
  },
  {
    prop: RELATIONS_RELATION_TYPE,
    errorLabel: 'Relation Type',
    required: true,
  },
];

const RelationsTable = ({ entitiesList }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const isEditMode = useContext(EntityContext);
  const entityRelations = useSelector(getRelations);

  const addRow = () => {
    const newRelation = {
      rowId: uuidv4(),
      Id: '',
      RelationType: '',
      ObjectKey: '',
      ParentObjectKey: '',
      ParentTableObjectKeyColumn: '',
    };
    dispatch(addEntityRelation(newRelation));
  };

  const editRow = (params) => {
    dispatch(editEntityRelation(params));
    params.error && dispatch(validateEntityDesigner());
  };

  const deleteRow = (params) => {
    dispatch(deleteEntityRelation(params));
  };

  return (
    <Grid container spacing={1}>
      {isEditMode && (
        <Grid classes={{ item: classes.addButtonContainer }} item xs={12}>
          <span ui-ta={getUITA('Entity Relations', 'AddButton')}>
            <Button color="primary" variant="secondary" onClick={addRow}>
              <Plus fontSize="small" />
              &nbsp;
              {translateString('Add')}
            </Button>
          </span>
        </Grid>
      )}
      <Grid style={{ overflowX: 'auto' }} item xs={12}>
        <Table
          columns={columns}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          rowId="rowId"
          rows={entityRelations.map((relation) => ({
            ...relation,
            classes,
            isEditMode,
            fieldTableControlType: TABLE_RELATIONS_CONTROL_TYPE,
            entitiesList,
            relationTypes: RELATION_TYPES,
            translateString,
            editRow,
            deleteRow,
          }))}
          rowsPerPageOptions={[20, 50, 100, translateString('All')]}
          defaultRowsPerPage={20}
          showFilterIcon
          tablePaginationProps={{
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${translateString('of')} ${count}`,
            truncate: true,
            labelRowsPerPage: translateString('Rows'),
            pageText: translateString('Page'),
            ofText: translateString('of'),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default RelationsTable;
